import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/adminAuth';
import { toast } from 'sonner';
import { 
  HardDrive, Cloud, Database, RefreshCw, Trash2, Download,
  FileText, Image, Archive, AlertTriangle, CheckCircle, Loader2,
  Calendar, Filter, BarChart3, Mail
} from 'lucide-react';
import { HeaderSkeleton, TableSkeleton } from './skeletons/Skeletons';
import GmailConnect from './GmailConnect';

export default function AdminSettingsPage() {
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteRange, setDeleteRange] = useState({ start: '', end: '' });
  const [deleting, setDeleting] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [previewingDelete, setPreviewingDelete] = useState(false);

  const fetchStorageStats = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await adminApi.get('/api/admin/storage/stats');
      setStorageStats(response.data);
    } catch (error) {
      console.error('Error fetching storage stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStorageStats();
  }, [fetchStorageStats]);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePreviewDelete = async () => {
    if (!deleteRange.start || !deleteRange.end) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setPreviewingDelete(true);
    try {
      const response = await adminApi.get('/api/admin/storage/preview-delete', {
        params: {
          start_date: deleteRange.start,
          end_date: deleteRange.end
        }
      });
      setFilesToDelete(response.data.files || []);
    } catch (error) {
      toast.error('Failed to preview files for deletion');
    } finally {
      setPreviewingDelete(false);
    }
  };

  const handleDeleteFiles = async () => {
    if (filesToDelete.length === 0) {
      toast.error('No files selected for deletion');
      return;
    }

    setDeleting(true);
    try {
      await adminApi.delete('/api/admin/storage/bulk-delete', {
        data: {
          start_date: deleteRange.start,
          end_date: deleteRange.end
        }
      });
      toast.success(`Successfully deleted ${filesToDelete.length} files`);
      setDeleteModal(false);
      setFilesToDelete([]);
      setDeleteRange({ start: '', end: '' });
      fetchStorageStats(true);
    } catch (error) {
      toast.error('Failed to delete files');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-settings-loading">
        <HeaderSkeleton withActions={false} />
        <TableSkeleton rows={6} columns={3} withHeader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-settings-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Settings</h1>
          <p className="text-neutral-500 mt-1">Manage storage and system settings</p>
        </div>
        <button
          onClick={() => fetchStorageStats(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors disabled:opacity-50"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Storage Monitor Section */}
      <div className="soft-card overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Storage Monitor
          </h2>
          <p className="text-pink-100 text-sm mt-1">Live storage usage across all platforms</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* AWS S3 Storage */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Cloud className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1a1a2e]">AWS S3</h3>
                  <p className="text-sm text-neutral-500">Cloud Storage</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Total Files</span>
                  <span className="font-semibold">{storageStats?.s3?.total_files || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Storage Used</span>
                  <span className="font-semibold">{formatBytes(storageStats?.s3?.total_size)}</span>
                </div>
              </div>
            </div>

            {/* MongoDB Storage */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1a1a2e]">MongoDB</h3>
                  <p className="text-sm text-neutral-500">Database Storage</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Collections</span>
                  <span className="font-semibold">{storageStats?.mongodb?.collections || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Total Documents</span>
                  <span className="font-semibold">{storageStats?.mongodb?.total_documents?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Storage Size</span>
                  <span className="font-semibold">{formatBytes(storageStats?.mongodb?.storage_size)}</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1a1a2e]">Quick Stats</h3>
                  <p className="text-sm text-neutral-500">Summary</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Total Storage</span>
                  <span className="font-semibold">{formatBytes((storageStats?.s3?.total_size || 0) + (storageStats?.mongodb?.storage_size || 0))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Images</span>
                  <span className="font-semibold">{storageStats?.s3?.by_type?.images || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Documents</span>
                  <span className="font-semibold">{storageStats?.s3?.by_type?.documents || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Integrations */}
      <div className="soft-card overflow-hidden" data-testid="email-integrations-section">
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Integrations
          </h2>
          <p className="text-indigo-100 text-sm mt-1">
            Connect Gmail accounts that send transactional mail on behalf of Girgit.
            Invoices &amp; payments go from <strong>accounts@</strong>; offer letters, status updates &amp; HR chat pings go from <strong>hr@</strong>.
          </p>
        </div>
        <div className="p-6">
          <GmailConnect />
        </div>
      </div>

      {/* File Types Breakdown */}
      <div className="soft-card overflow-hidden">
        <div className="px-6 py-4 border-b border-white/40">
          <h3 className="font-semibold text-[#1a1a2e]">File Types Breakdown</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
              <Image className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-neutral-500">Images</p>
                <p className="font-semibold">{storageStats?.s3?.by_type?.images || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl">
              <FileText className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm text-neutral-500">PDFs</p>
                <p className="font-semibold">{storageStats?.s3?.by_type?.pdfs || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
              <FileText className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-neutral-500">Documents</p>
                <p className="font-semibold">{storageStats?.s3?.by_type?.documents || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-white/40 rounded-xl">
              <Archive className="w-8 h-8 text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500">Other</p>
                <p className="font-semibold">{storageStats?.s3?.by_type?.other || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Management */}
      <div className="soft-card overflow-hidden">
        <div className="px-6 py-4 border-b border-white/40">
          <h3 className="font-semibold text-[#1a1a2e]">Storage Management</h3>
          <p className="text-sm text-neutral-500 mt-1">Clean up old files by date range</p>
        </div>
        <div className="p-6">
          <button
            onClick={() => setDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Bulk Delete Files
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/40 flex items-center justify-between">
              <h3 className="font-semibold text-[#1a1a2e] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Bulk Delete Files
              </h3>
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setFilesToDelete([]);
                  setDeleteRange({ start: '', end: '' });
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={deleteRange.start}
                    onChange={(e) => setDeleteRange({ ...deleteRange, start: e.target.value })}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                    data-testid="delete-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={deleteRange.end}
                    onChange={(e) => setDeleteRange({ ...deleteRange, end: e.target.value })}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                    data-testid="delete-end-date"
                  />
                </div>
              </div>

              <button
                onClick={handlePreviewDelete}
                disabled={previewingDelete || !deleteRange.start || !deleteRange.end}
                className="w-full px-4 py-2 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="preview-delete-btn"
              >
                {previewingDelete ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Filter className="w-4 h-4" />
                )}
                Preview Files
              </button>

              {filesToDelete.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-800">
                    <strong>{filesToDelete.length}</strong> files will be permanently deleted.
                    This action cannot be undone.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setDeleteModal(false);
                    setFilesToDelete([]);
                    setDeleteRange({ start: '', end: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-white/40 text-neutral-700 rounded-xl hover:bg-white/60 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFiles}
                  disabled={deleting || filesToDelete.length === 0}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete Files
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
